import { useServiceFactory } from '@euler/services/factory';
import { useCreation } from '@euler/utils/hooks/useCreation';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { BehaviorSubject, combineLatest } from 'rxjs';
import { PracticeSession } from './usePracticeSession';

/** Asked for once the trainee has been quiet this long... */
const kPrefetchMs = 3_000;
/** ...and shown after this long. */
const kShowMs = 8_000;

/**
 * A dimmed hint for the trainee's next line when they go quiet, or at
 * once on 💡. Silence counts from the moment it is their turn and the
 * customer has stopped speaking; holding to talk or typing starts it over.
 * A line sent while a hint showed is marked as hinted. Off in an exam
 * (`attemptId` null); a replay shows its better line from the start.
 */
export function useSilenceHint(
  session: PracticeSession,
  speaking$: BehaviorSubject<boolean>,
  attemptId: string | null,
  fixedHint: string | null,
) {
  const { coachingService } = useServiceFactory();
  const hint$ = useCreation(
    () => new BehaviorSubject<string | null>(fixedHint),
    [fixedHint],
  );
  /** Bumped each trainee turn: a hint fetched for an earlier one is stale. */
  const turn = useRef(0);
  const fetched = useRef<{ turn: number; hint: Promise<string | null> } | null>(
    null,
  );
  const timers = useRef<ReturnType<typeof setTimeout>[]>([]);
  const shown = useRef(!!fixedHint);
  const holding = useRef(false);
  const clearTimers = () => {
    timers.current.forEach(clearTimeout);
    timers.current = [];
  };

  const fetchHint = useCallback(() => {
    if (!attemptId) return Promise.resolve(null);
    const mine = turn.current;
    if (fetched.current?.turn !== mine) {
      const hint = coachingService
        .practiceHint(attemptId)
        .then(x => x.hint)
        .catch(error => {
          console.error('coaching practice hint failed', error);
          if (fetched.current?.turn === mine) fetched.current = null;
          return null;
        });
      fetched.current = { turn: mine, hint };
    }
    return fetched.current.hint;
  }, [attemptId, coachingService]);

  const show = useCallback(async () => {
    const mine = turn.current;
    const hint = await fetchHint();
    if (
      !hint ||
      mine !== turn.current ||
      session.phase$.getValue().kind !== 'listening'
    )
      return;
    shown.current = true;
    hint$.next(hint);
  }, [fetchHint, hint$, session]);

  const quiet = useCallback(
    () =>
      session.phase$.getValue().kind === 'listening' &&
      !speaking$.getValue() &&
      !holding.current,
    [session, speaking$],
  );

  const arm = useCallback(() => {
    clearTimers();
    if (!attemptId || !quiet()) return;
    timers.current = [
      setTimeout(() => void fetchHint(), kPrefetchMs),
      setTimeout(() => void show(), kShowMs),
    ];
  }, [attemptId, fetchHint, quiet, show]);

  useEffect(() => {
    const subscription = combineLatest([session.phase$, speaking$]).subscribe(
      ([phase]) => {
        if (phase.kind === 'replying') {
          // The next turn asks for its own hint; a replay's better line was
          // for its first.
          turn.current += 1;
          shown.current = false;
          hint$.next(null);
        }
        arm();
      },
    );
    return () => {
      subscription.unsubscribe();
      clearTimers();
    };
  }, [arm, hint$, session, speaking$]);

  const press = useCallback(() => {
    holding.current = true;
    clearTimers();
  }, []);
  const settle = useCallback(() => {
    holding.current = false;
    arm();
  }, [arm]);
  const dismiss = useCallback(() => hint$.next(null), [hint$]);
  const takeAfterHint = useCallback(() => shown.current, []);

  return useMemo(
    () => ({
      hint$,
      /** 💡: the hint now. */
      ask: show,
      /** Typing: the silence starts over. */
      activity: arm,
      /** Holding to talk: no hint while the trainee speaks. */
      press,
      /** Released or cancelled: silence counts again. */
      settle,
      dismiss,
      /** Whether the line about to be sent follows a hint. */
      takeAfterHint,
    }),
    [arm, dismiss, hint$, press, settle, show, takeAfterHint],
  );
}
